import React, { useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { openDispatchingModal, patchSaleLot } from "actions";

import { MultiButton } from "components/Button";
import { PreventPropagation } from "components/Events";

import {
  ClearingSaleLotStatus,
  RCTI,
  TAX_INVOICE,
  TAX_RECEIPT,
} from "constants/clearingSaleAttributes";
import {
  ConsignmentModalSection,
  ModalTypes,
  SaleLotModalSection,
} from "constants/navigation";
import { ConsignmentPermission } from "constants/permissions";

import {
  getClearingSaleBuyerInvoiceUrl,
  getClearingSaleBuyerReceiptUrl,
  getClearingSaleRecipientCreatedTaxInvoiceUrl,
  openEditBusinessModal,
  openEditConsignmentModal,
  openEditSaleLotModal,
  openEmailReportModal,
} from "lib/navigation";

import {
  getActiveRole,
  getBusinesses,
  getConsignmentById,
  getPaymentIdsByFromBusinessId,
} from "selectors";

import {
  getBuyerBalanceSummaryByBuyerId,
  getClearingSaleInvoiceeBusinessIdsByBuyerId,
} from "selectors/clearingSaleInvoice";

import {
  buildSplitEmailActions,
  buildSplitViewActions,
} from "lib/clearingSaleBuyerReportActions";

import { useHasPermission } from "hooks/useHasPermission";

const CardOptionsMenu = ({ options }) => (
  <PreventPropagation>
    <MultiButton buttons={options} />
  </PreventPropagation>
);

export const SaleLotMenu = ({ saleLotId }) => {
  const dispatch = useDispatch();

  const edit = () =>
    openEditSaleLotModal(saleLotId, SaleLotModalSection.GENERAL);

  const unSell = () => {
    const patch = {
      total_price_cents: 0,
      buyer_id: null,
      buyer_way: null,
      id: saleLotId,
      clearing_sale_attributes: { status: ClearingSaleLotStatus.ACTIVE },
    };
    dispatch(patchSaleLot(patch));
  };

  const setAsPaid = () =>
    dispatch(
      patchSaleLot({
        id: saleLotId,
        clearingSaleAttributes: { status: ClearingSaleLotStatus.PAID },
      }),
    );

  const options = [
    {
      title: "Edit Lot",
      onClick: edit,
      dataTour: "editLot",
    },
    {
      title: "Set as Paid",
      onClick: setAsPaid,
      dataTour: "setAsPaid",
    },
    {
      title: "Unsell",
      onClick: unSell,
      dataTour: "unSell",
    },
  ];

  return <CardOptionsMenu options={options} />;
};

export const ConsignmentMenu = ({ consignmentId, disableAdd }) => {
  const vendorId = useSelector(
    state => getConsignmentById(consignmentId)(state)?.vendor_id,
  );
  const userRoleSlug = useSelector(state => getActiveRole(state).slug);

  const editVendorConsignment = () =>
    openEditConsignmentModal(
      consignmentId,
      ConsignmentModalSection.GENERAL,
      true,
    );
  const editVendor = () => openEditBusinessModal(vendorId);

  const addSaleLot = () =>
    openEditSaleLotModal(
      null,
      SaleLotModalSection.GENERAL,
      "",
      consignmentId,
      null,
      window.location.hash,
    );

  const RCTIUrl = getClearingSaleRecipientCreatedTaxInvoiceUrl;

  const emailRCTI = () =>
    openEmailReportModal(
      RCTIUrl(vendorId),
      RCTI,
      vendorId,
      null,
      window.location.hash,
    );

  const openRCTI = () => {
    window.open(RCTIUrl(vendorId, userRoleSlug));
  };

  const hasUpdateConsignmentPermission = useHasPermission(
    getConsignmentById(consignmentId),
    ConsignmentPermission.update,
  );

  const options = [
    {
      title: `${
        hasUpdateConsignmentPermission ? "Edit" : "View"
      } Vendor Consignment`,
      onClick: editVendorConsignment,
      dataTour: "editVendorConsignment",
    },
    {
      title: `View ${RCTI}`,
      onClick: openRCTI,
      dataTour: "recipientCreatedTaxInvoice",
    },
    {
      title: `Email ${RCTI}`,
      onClick: emailRCTI,
      dataTour: "emailRecipientCreatedTaxInvoice",
    },
    {
      title: "Business Details",
      onClick: editVendor,
      dataTour: "businessDetails",
    },
  ].filter(Boolean);

  if (!disableAdd) {
    options.unshift({
      title: "Add Lot",
      onClick: addSaleLot,
      dataTour: "addLot",
    });
  }
  return <CardOptionsMenu options={options} />;
};

export const BuyerMenu = ({ businessId }) => {
  const balanceSummary = useSelector(
    getBuyerBalanceSummaryByBuyerId(businessId),
  );
  const dueAmountCents = balanceSummary?.totals?.dueCents ?? null;

  const paymentCount =
    useSelector(getPaymentIdsByFromBusinessId(businessId))?.length ?? 0;

  const invoiceeBusinessIds = useSelector(
    getClearingSaleInvoiceeBusinessIdsByBuyerId(businessId),
  );
  const businessesById = useSelector(getBusinesses);
  const invoiceeBusinesses = useMemo(
    () => invoiceeBusinessIds.map(id => businessesById[id] || null),
    [invoiceeBusinessIds, businessesById],
  );

  const dispatch = useDispatch();

  const editBusiness = () => openEditBusinessModal(businessId);

  const openAddPayment = () =>
    dispatch(
      openDispatchingModal(
        ModalTypes.EditPayment,
        window.location.hash,
        { values: { totalAmountCents: dueAmountCents } },
        { fromBusinessId: businessId },
      ),
    );

  const openPayments = () =>
    dispatch(
      openDispatchingModal(
        ModalTypes.BuyerPaymentSummary,
        window.location.hash,
        null,
        { buyerId: businessId },
      ),
    );

  const userRoleSlug = useSelector(state => getActiveRole(state).slug);

  const invoiceUrl = getClearingSaleBuyerInvoiceUrl;

  const receiptUrl = getClearingSaleBuyerReceiptUrl;

  const emailReport = (reportUrl, reportName, invoiceeBusinessId) =>
    openEmailReportModal(
      reportUrl,
      reportName,
      invoiceeBusinessId,
      null,
      window.location.hash,
    );

  const isSplitBuyer = invoiceeBusinessIds.length > 1;

  // A buyer whose lots are entirely redirected to one other business (e.g. Cash
  // Sales) resolves to exactly one invoicee, which isn't a "split" - but that
  // invoicee is still not this business, so its id must be used, not businessId.
  const soleInvoiceeBusinessId = invoiceeBusinessIds[0] ?? businessId;

  const viewActions = isSplitBuyer
    ? buildSplitViewActions({
        businessId,
        userRoleSlug,
        invoiceeBusinesses,
        invoiceUrl,
        receiptUrl,
      })
    : [
        {
          title: `View ${TAX_INVOICE}`,
          onClick: () =>
            window.open(invoiceUrl(soleInvoiceeBusinessId, userRoleSlug)),
        },
        {
          title: `View ${TAX_RECEIPT}`,
          onClick: () =>
            window.open(receiptUrl(soleInvoiceeBusinessId, userRoleSlug)),
        },
      ];

  const emailActions = isSplitBuyer
    ? buildSplitEmailActions({
        businessId,
        invoiceeBusinesses,
        invoiceUrl,
        receiptUrl,
        emailReport,
      })
    : [
        {
          title: `Email ${TAX_INVOICE}`,
          onClick: () =>
            emailReport(
              invoiceUrl(soleInvoiceeBusinessId),
              TAX_INVOICE,
              soleInvoiceeBusinessId,
            ),
        },
        {
          title: `Email ${TAX_RECEIPT}`,
          onClick: () =>
            emailReport(
              receiptUrl(soleInvoiceeBusinessId),
              TAX_RECEIPT,
              soleInvoiceeBusinessId,
            ),
        },
      ];

  const options = [
    dueAmountCents > 0 && {
      title: "Add Payment",
      onClick: openAddPayment,
    },
    {
      title: `View Payments (${paymentCount})`,
      onClick: openPayments,
    },
    ...viewActions,
    ...emailActions,
    {
      title: "Business Details",
      onClick: editBusiness,
      dataTour: "businessDetails",
    },
  ].filter(Boolean);

  return <CardOptionsMenu options={options} />;
};
